import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Bank } from '@ap/interface-bspc-core';

import {
    ActieveConfiguratie,
    Configuratie,
    ConfiguratieType,
    Kanaal,
    LijstVanActieveConfiguraties,
    LijstVanConfiguratieLogs,
    LijstVanConfiguraties,
    LijstVanConfiguratieTypes,
    LijstVanKanalen,
    RfcInformatie
} from './kanaalconfiguratie-beheer.model';

@Injectable({ providedIn: 'root' })
export class KanaalconfiguratieBeheerResource {
    private readonly baseUrl: string = '/services/apic-kanaalconfiguratie/api';

    public constructor(private readonly httpClient: HttpClient) {}

    public geefActieveConfiguraties(
        kanaalNamen: string[],
        configuratieNamen?: string[],
        bank?: Bank
    ): Observable<LijstVanActieveConfiguraties> {
        let httpHeaders = new HttpHeaders();
        if (bank) {
            httpHeaders = httpHeaders.set('X-Bank', bank);
        }

        let httpParams = new HttpParams();
        for (const kanaalNaam of kanaalNamen) {
            httpParams = httpParams.append('kanaal', kanaalNaam);
        }
        if (!!configuratieNamen) {
            for (const configuratieNaam of configuratieNamen) {
                httpParams = httpParams.append('configuratie', configuratieNaam);
            }
        }

        return this.httpClient.get<LijstVanActieveConfiguraties>(`${this.baseUrl}/actieve-configuraties`, {
            headers: httpHeaders,
            params: httpParams
        });
    }

    public geefActieveConfiguratie(kanaalNaam: string, configuratieNaam: string, bank?: Bank): Observable<ActieveConfiguratie> {
        let httpHeaders = new HttpHeaders();
        if (bank) {
            httpHeaders = httpHeaders.set('X-Bank', bank);
        }
        const httpParams = new HttpParams().set('kanaal', kanaalNaam);
        return this.httpClient.get<ActieveConfiguratie>(`${this.baseUrl}/actieve-configuraties/${configuratieNaam}`, {
            headers: httpHeaders,
            params: httpParams
        });
    }

    public geefConfiguraties(
        bank?: Bank,
        kanaalNaam?: string,
        configuratieType?: string,
        configuratieNaam?: string
    ): Observable<LijstVanConfiguraties> {
        const httpHeaders = new HttpHeaders();
        if (bank) {
            httpHeaders.append('X-Bank', bank);
        }
        let httpParams = new HttpParams();
        if (kanaalNaam) {
            httpParams = httpParams.append('kanaal', kanaalNaam);
        }
        if (configuratieNaam) {
            httpParams = httpParams.append('configuratie', configuratieNaam);
        }
        if (configuratieType) {
            httpParams = httpParams.append('configuratie_type', configuratieType);
        }
        return this.httpClient.get<LijstVanConfiguraties>(`${this.baseUrl}/configuraties`, {
            headers: httpHeaders,
            params: httpParams
        });
    }

    public geefConfiguratie(id: string): Observable<Configuratie> {
        return this.httpClient.get<Configuratie>(`${this.baseUrl}/configuraties/${id}`);
    }

    public creeerConfiguratie(configuratie: Configuratie, rfc: string): Observable<void> {
        const httpParams = new HttpParams().set('rfc', rfc);
        return this.httpClient.post<void>(`${this.baseUrl}/configuraties`, configuratie, { params: httpParams });
    }

    public vervangConfiguratie(id: string, configuratie: Configuratie, rfc: string): Observable<void> {
        const httpParams = new HttpParams().set('rfc', rfc);
        return this.httpClient.put<void>(`${this.baseUrl}/configuraties/${id}`, configuratie, {
            params: httpParams
        });
    }

    public verwijderConfiguratie(id: string, rfc: string): Observable<void> {
        const httpParams = new HttpParams().set('rfc', rfc);
        return this.httpClient.delete<void>(`${this.baseUrl}/configuraties/${id}`, {
            params: httpParams
        });
    }

    public geefConfiguratieLogs(kanaal?: string, configuratie?: string): Observable<LijstVanConfiguratieLogs> {
        let httpParams = new HttpParams();
        if (kanaal) {
            httpParams = httpParams.append('kanaal', kanaal);
        }
        if (configuratie) {
            httpParams = httpParams.append('configuratie', configuratie);
        }
        return this.httpClient.get<LijstVanConfiguratieLogs>(`${this.baseUrl}/configuratie-logs`, { params: httpParams });
    }

    public geefConfiguratieTypes(): Observable<LijstVanConfiguratieTypes> {
        return this.httpClient.get<LijstVanConfiguratieTypes>(`${this.baseUrl}/configuratie-types`);
    }

    public geefConfiguratieType(id: string): Observable<ConfiguratieType> {
        return this.httpClient.get<ConfiguratieType>(`${this.baseUrl}/configuratie-types/${id}`);
    }

    public creeerConfiguratieType(configuratieType: ConfiguratieType): Observable<void> {
        return this.httpClient.post<void>(`${this.baseUrl}/configuratie-types`, configuratieType);
    }

    public vervangConfiguratieType(id: string, configuratieType: ConfiguratieType): Observable<void> {
        return this.httpClient.put<void>(`${this.baseUrl}/configuratie-types/${id}`, configuratieType);
    }

    public verwijderConfiguratieType(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/configuratie-types/${id}`);
    }

    public geefKanalen(): Observable<LijstVanKanalen> {
        return this.httpClient.get<LijstVanKanalen>(`${this.baseUrl}/kanalen`);
    }

    public geefKanaal(id: string): Observable<Kanaal> {
        return this.httpClient.get<Kanaal>(`${this.baseUrl}/kanalen/${id}`);
    }

    public creeerKanaal(kanaal: Kanaal): Observable<void> {
        return this.httpClient.post<void>(`${this.baseUrl}/kanalen`, kanaal);
    }

    public vervangKanaal(id: string, kanaal: Kanaal): Observable<void> {
        return this.httpClient.put<void>(`${this.baseUrl}/kanalen/${id}`, kanaal);
    }

    public verwijderKanaal(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/kanalen/${id}`);
    }

    public geefRfcInformatie(rfcNumber: string): Observable<RfcInformatie> {
        return this.httpClient.get<RfcInformatie>(`${this.baseUrl}/rfc/${rfcNumber}`);
    }
}
